import { useParams, useNavigate } from "react-router-dom"
import MainTitle from "../../components/template/MainTitle"
import ButtonWrap from "../../components/template/ButtonWrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowLeft, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons"
import SubTitle from "../../components/template/SubTitle"
import React, { useState, useEffect } from "react"
import api from "../../components/utils/api"
import CustomSoundInformation from "./CustomSoundInformation"
import CustomSoundStatusPill from "./CustomSoundStatusPill"
import Spinner from "../../components/template/Spinner"
import CustomSoundStatusGraph from "./CustomSoundStatusGraph"
import DirectLink from "../../components/template/DirectLink"
import Modal from "../../components/template/Modal"
import MergeRulePairTemplate from "../PostActionPage/components/MergeRulePairTemplate"
import ThresholdModal from "../PostActionPage/subroutes/threshold/ThresholdModal"

const MergeRuleLink = ({ mergeRuleId }) => {
    const [mergeRulePairs, setMergeRulePairs] = useState([])
    const [modalVisible, setModalVisible] = useState(false);
    const openModal = () => { setModalVisible(true); }
    const closeModal = () => { setModalVisible(false); }

    useEffect(() => {
        const fetchMergeRule = async () => {
            try {
                const response = await api.get(`/api/merge-rules/${mergeRuleId}`)
                setMergeRulePairs(response.data.tags)
            } catch (error) {
                console.error(error)
            }
        }
        if (mergeRuleId) {
            fetchMergeRule()
        }
    }, [mergeRuleId])

    return (
        <div>
            <button type="button" className="text-gray-700 hover:text-purple-500" onClick={openModal}><FontAwesomeIcon icon={faMagnifyingGlass} size="lg" /></button>
            {modalVisible && <Modal
                visible={modalVisible}
                closable={true}
                maskClosable={true}
                onClose={closeModal}
            >
                <div>
                    <MainTitle title="Merge Rule" />
                    {mergeRulePairs.map((rep, i) => <MergeRulePairTemplate
                        key={i}
                        representativeClass={rep.name}
                        pairedClasses={rep.merged_classes}
                    />)}
                </div>
            </Modal>}
        </div>
    )
}

const ThresholdLink = ({ mergeRuleId }) => {
    const [selectedThreshold, setSelectedThreshold] = useState({})
    const [modalVisible, setModalVisible] = useState(false);
    const openModal = () => { setModalVisible(true); }
    const closeModal = () => { setModalVisible(false); }

    useEffect(() => {
        const fetchMergeRule = async () => {
            try {
                const response = await api.get(`/api/merge-rules/${mergeRuleId}/thresholds`)
                setSelectedThreshold(response.data.thresholds[0])
            } catch (error) {
                console.error(error)
            }
        }
        if (mergeRuleId) {
            fetchMergeRule()
        }
    }, [mergeRuleId])

    return (
        <div>
            <button type="button" className="text-gray-700 hover:text-purple-500" onClick={openModal}><FontAwesomeIcon icon={faMagnifyingGlass} size="lg" /></button>
            {modalVisible && <Modal
                visible={modalVisible}
                closable={true}
                maskClosable={true}
                onClose={closeModal}
            >
                <ThresholdModal selectedThreshold={selectedThreshold} mergeRuleId={mergeRuleId} />
            </Modal>}
        </div>
    )
}

const CustomSoundDetail = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const [requestInfo, setRequestInfo] = useState()
    const [convertedModelPath, setConvertedModelPath] = useState()
    const [servicePath, setServicePath] = useState()

    useEffect(() => {
        const fetchRequestInfo = async () => {
            try {
                const response = await api.get(`/api/custom-sounds/${id}`)
                setRequestInfo(response.data)
            } catch (error) {
                console.error(error)
            }
        }
        if (id) {
            fetchRequestInfo()
        }
    }, [id])

    useEffect(() => {
        const fetchConvertedModelPath = async () => {
            try {
                const response = await api.get(`/api/merge-rules/${requestInfo.merge_rule_id}/converted-models/framework-map`)
                setConvertedModelPath(response.data.converted_model_map.tflite_float32.path)
            } catch (error) {
                console.error(error)
            }
        }
        if (requestInfo?.merge_rule_id) {
            fetchConvertedModelPath()
        }
    }, [requestInfo])

    useEffect(() => {
        const fetchServicePath = async () => {
            try {
                const response = await api.get(`/api/eats-services/${requestInfo.service_id}`)
                setServicePath(response.data.path)
            } catch (error) {
                console.error(error)
            }
        }
        if (requestInfo?.service_id) {
            fetchServicePath()
        }
    }, [requestInfo])

    return (
        <div className='default-background'>
            <div className="flex justify-between items-center my-5">
                <div className="flex items-center gap-2">
                    <MainTitle title={`Custom Sound - Request ${id}`} />
                    {requestInfo && <CustomSoundStatusPill value={requestInfo.status} />}
                </div>
                <ButtonWrap contents={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => navigate('/custom-sound')} />
            </div>
            <SubTitle title="Request Information" />
            {requestInfo && <CustomSoundInformation items={[
                { label: "Custom Sound ID", value: requestInfo.custom_sound_id },
                { label: "User ID", value: requestInfo.org_uuid },
                { label: "Environment", value: requestInfo.env },
                { label: "Request Type", value: requestInfo.req_type },
                { label: "Requested At", value: requestInfo.requested_at },
                { label: "Completed At", value: requestInfo.status === "completed" ? requestInfo.completed_at : <Spinner /> },
            ]} />}
            <SubTitle title="Request Progress" />
            {requestInfo && <CustomSoundStatusGraph currentStatus={requestInfo.status} />}
            {requestInfo?.status === "completed" ? <><SubTitle title="Service Information" />
                {requestInfo && <CustomSoundInformation items={[
                    { label: "Service ID", value: requestInfo.service_id },
                    { label: "Service Path", value: <DirectLink path={`https://console.cloud.google.com/storage/browser/${servicePath}`} title={`Direct link for the service`} /> },
                ]} />}</> : <></>}
            <SubTitle title="Resources" />
            <ul className="list-disc list-inside grid md:grid-cols-2 grid-cols-1 gap-4 text-center my-2">
                {requestInfo && [
                    { label: "Merge Rule", value: requestInfo.status === "requested" ? <Spinner /> : <MergeRuleLink mergeRuleId={requestInfo.merge_rule_id} /> },
                    { label: "Thresholds", value: requestInfo.status === "requested" ? <Spinner /> : <ThresholdLink mergeRuleId={requestInfo.merge_rule_id} /> },
                    {
                        label: "Converted Model (GCS)", value:
                            ["converted", "exported", "completed"].includes(requestInfo.status) ?
                                <DirectLink path={`https://console.cloud.google.com/storage/browser/${convertedModelPath}`} title={`Direct link for the converted model`} /> : <Spinner />
                    },
                    {
                        label: "Service (GCS)", value:
                            requestInfo.status === "completed" ?
                                <DirectLink path={`https://console.cloud.google.com/storage/browser/${servicePath}`} title={`Direct link for the service`} /> : <Spinner />
                    },
                ].map((e, i) => <li key={i} className="flex items-center gap-4 justify-between border rounded px-6 py-4">
                    <p className="font-medium">{e.label}</p> {e.value}
                </li>)}
            </ul>

        </div>
    )
}

export default CustomSoundDetail